import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import axios from "axios";
import { API } from "@/apiConfig";

// Animation fiable : animate au montage (pas whileInView qui peut rater sur build statique)
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: "easeOut" },
});

const DEFAULTS = {
  hero_kicker: "La sophrologie caycédienne",
  page_title: "Une science de la conscience et du bien-être",
  intro_text: "Fondée dans les années 1960 par le Dr Alfonso Caycedo, neuropsychiatre, la sophrologie caycédienne est une méthode rigoureuse qui allie science et pratique pour harmoniser corps, esprit et conscience.",
  main_image: "https://images.unsplash.com/photo-1718244200930-3ce5b5b40607?q=85&w=1200&auto=format&fit=crop",
  main_image_alt: "Zen garden",
  section_title: "Qu'est-ce que la sophrologie caycédienne ?",
  description: "La sophrologie caycédienne se distingue des autres approches par sa rigueur scientifique et sa progression structurée en degrés. Elle intègre des éléments de phénoménologie, de yoga, de bouddhisme zen et de médecines orientales.",
  description_2: "Sa méthode repose sur des exercices de Relaxation Dynamique — alternance de mouvements corporels et de relaxation — et sur la sophronisation, un état de conscience modifiée entre veille et sommeil.",
  description_3: "La sophrologie caycédienne n'est pas une thérapie, mais un entraînement de la conscience qui renforce les ressources intérieures et améliore la qualité de vie.",
  piliers_kicker: "Les fondements",
  piliers_title: "Les quatre piliers",
  benefits_title: "Pour quoi pratiquer ?",
  benefits_desc: "La sophrologie caycédienne aide dans de nombreuses situations de vie.",
  benefits_list: "Stress & anxiété\nTroubles du sommeil\nConfiance en soi\nPréparation mentale\nDouleurs chroniques\nTroubles émotionnels\nÉpanouissement\nTransition de vie",
  benefits_button_label: "Découvrir les accompagnements",
};

const piliers = [
  { num: "01", title: "Le corps", desc: "La sophrologie caycédienne part du corps. Les exercices de relaxation dynamique permettent de prendre conscience des sensations physiques et de libérer les tensions accumulées." },
  { num: "02", title: "La conscience", desc: "Par des techniques de sophronisation, nous travaillons sur l'état de conscience entre veille et sommeil, un état particulièrement propice au changement et à l'intégration." },
  { num: "03", title: "Les valeurs", desc: "La sophrologie caycédienne accompagne la découverte et l'affirmation des valeurs personnelles, pilier d'une vie épanouie et alignée." },
  { num: "04", title: "La présence", desc: "Être pleinement présent à soi-même et au monde : c'est l'art de la sophrologie caycédienne, une invitation à vivre avec plus de conscience et de profondeur." },
];

export default function Sophrologie() {
  const [c, setC] = useState({});

  useEffect(() => {
    document.title = "La Sophrologie Caycédienne — Eudemonie";
    axios.get(`${API}/public/pages/sophrologie-caycedienne`).then(r => { if (r.data?.content) setC(r.data.content); }).catch(() => {});
  }, []);

  const g = key => c[key] || DEFAULTS[key];
  const dynamicPiliers = piliers.map((p, i) => ({
    ...p,
    title: g(`pilier${i + 1}_title`) || p.title,
    desc: g(`pilier${i + 1}_desc`) || p.desc,
  }));
  const benefits = String(g("benefits_list") || "").split("\n").map(b => b.trim()).filter(Boolean);

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="section-gap bg-bone">
        <div className="section-container">
          <div className="max-w-3xl">
            <motion.p {...fadeUp()} className="font-manrope text-xs text-sage tracking-widest uppercase mb-3">{g("hero_kicker")}</motion.p>
            <motion.h1 {...fadeUp(0.1)} className="font-playfair text-5xl md:text-6xl text-charcoal mb-6">{g("page_title")}</motion.h1>
            <motion.p {...fadeUp(0.2)} className="font-manrope text-charcoal-muted text-lg leading-relaxed">
              {g("intro_text")}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Image + intro */}
      <section className="section-gap bg-bone-light">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp()}>
              <img src={g("main_image")} alt={g("main_image_alt")} className="rounded-[2rem] w-full h-[400px] object-cover shadow-lg" />
            </motion.div>
            <motion.div {...fadeUp(0.2)}>
              <h2 className="font-playfair text-3xl md:text-4xl text-charcoal mb-6">{g("section_title")}</h2>
              <p className="font-manrope text-charcoal-muted leading-relaxed mb-4">
                {g("description")}
              </p>
              <p className="font-manrope text-charcoal-muted leading-relaxed mb-4">
                {g("description_2")}
              </p>
              <p className="font-manrope text-charcoal-muted leading-relaxed">
                {g("description_3")}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Piliers */}
      <section className="section-gap bg-bone">
        <div className="section-container">
          <motion.div {...fadeUp()} className="text-center mb-16">
            <p className="font-manrope text-xs text-sage tracking-widest uppercase mb-3">{g("piliers_kicker")}</p>
            <h2 className="font-playfair text-4xl md:text-5xl text-charcoal">{g("piliers_title")}</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {dynamicPiliers.map((p, i) => (
              <motion.div key={i} {...fadeUp(i * 0.1)} data-testid={`pilier-${i}`} className="card-soft flex gap-6">
                <span className="font-playfair text-5xl text-sage/20 leading-none shrink-0">{p.num}</span>
                <div>
                  <h3 className="font-playfair text-2xl text-charcoal mb-3">{p.title}</h3>
                  <p className="font-manrope text-sm text-charcoal-muted leading-relaxed">{p.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-gap bg-sage">
        <div className="section-container text-center">
          <motion.h2 {...fadeUp()} className="font-playfair text-4xl md:text-5xl text-white mb-4">{g("benefits_title")}</motion.h2>
          <motion.p {...fadeUp(0.1)} className="font-manrope text-white/75 max-w-xl mx-auto mb-12">{g("benefits_desc")}</motion.p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {benefits.map((b, i) => (
              <motion.div key={i} {...fadeUp(i * 0.05)} className="bg-white/10 rounded-2xl px-4 py-5 backdrop-blur-sm border border-white/20">
                <span className="font-manrope text-sm text-white">{b}</span>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp(0.4)} className="mt-12">
            <Link to="/accompagnements" className="rounded-full bg-white text-sage-dark hover:bg-bone px-8 py-4 font-manrope font-medium text-sm transition-all duration-300 shadow-md inline-flex items-center gap-2">
              {g("benefits_button_label")} <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
